import * as React from 'react';
import { connect } from 'react-redux';
import { get, isNil, sortBy, xor } from 'lodash';

import { navigate } from 'libs/router';

import SentimentAnalysisSecondaryFilter from '../sentiment-analysis-secondary-filter/SentimentAnalysisSecondayFilter';
import InfluencerSelector from 'components/influencer-selector/InfluencerSelector';

import { isDefined } from 'utils/helper/common';
import { getLocationUrl, getLocationWithExtendedQuery } from 'utils/client';
import { getDisplayDate, getDisplayDateRange } from 'utils/date';
import { __t, applyTranslations } from 'utils/language';
import { isDraft } from 'utils/subcampaign';

import { resetDeliverables, searchDeliverablesWithPosts } from 'modules/admin/reducers/admin-search-deliverables';
import { fetchSubcampaigns } from 'modules/campaigns/reducers/campaignSubcampaigns';

import './admin-sentiment-analysis-filters.scss';

import type { InfluencerSelector as InfluencerSelectorAlias, LocationWindow, MultiSelectItem, Subcampaign, TextEntry } from 'types';

import type { SyntheticEvent } from 'react';

applyTranslations(require('./admin-sentiment-analysis-filters.i18n.json'));

export type Query = {
  page?: number,
  taskTypes?: string,
  campaignId?: string,
  influencerId?: string,
  deliverableId?: string,
  sentimentClass?: string
};

export type SelectionQuery = {
  campaignId?: string,
  influencerId?: string,
  deliverableId?: string,
  page?: number,
  sentimentClass: Array<string>,
  taskTypes: string
};

type StateProps = {
  isTaskListLoading: boolean,
  tasksItems: Array<MultiSelectItem>,
  location: LocationWindow,
  subcampaignsList: Array<Subcampaign>
};

type DispatchProps = {
  dispatchFetchSubcampaigns: (campaignId: number) => Promise<void>,
  dispatchSearchDeliverables: (
    campaignId: number,
    influencerId: number,
    deliverableType?: string
  ) => Promise<void>,
  dispatchResetDeliverables: () => Promise<void>
};

type OwnProps = {
  campaignId?: number
}

type Props = OwnProps & StateProps & DispatchProps;

class SentimentAnalysisFilters extends React.Component<Props> {
  componentDidMount() {
    const { location, campaignId, dispatchFetchSubcampaigns } = this.props;
    const selection = this.getSelectionFromQuery(location.query);

    if (campaignId) {
      dispatchFetchSubcampaigns(campaignId);
    }

    this.loadDeliverables(selection.influencerId, selection.taskTypes);
  }

  componentDidUpdate(prevProps: Props) {
    const { location, campaignId } = this.props;

    const prevSelection = this.getSelectionFromQuery(prevProps.location.query);
    const selection = this.getSelectionFromQuery(location.query);

    if (prevProps.campaignId !== campaignId) {
      this.loadDeliverables(selection.influencerId, selection.taskTypes);
    }

    if (prevSelection.influencerId !== selection.influencerId || prevSelection.taskTypes != selection.taskTypes) {
      this.loadDeliverables(selection.influencerId, selection.taskTypes);
    }
  }

  componentWillUnmount() {
    const { dispatchResetDeliverables } = this.props;

    dispatchResetDeliverables();
  }

  private loadDeliverables(influencerId?: string | null, taskTypes?: string) {
    const { campaignId, dispatchSearchDeliverables, dispatchResetDeliverables } = this.props;

    if (campaignId && influencerId) {
      dispatchSearchDeliverables(Number(campaignId), Number(influencerId), taskTypes === 'all' ? undefined : taskTypes);
    } else {
      dispatchResetDeliverables();
    }
  }

  private getInfluencersForSelector(): Array<InfluencerSelectorAlias> {
    const { subcampaignsList } = this.props;
    const updatedSubcampaignsList = sortBy(subcampaignsList, ['updatedAt', 'creatorProfile.name'])
      .filter(subcampaign => !isDraft(subcampaign) && isNil(subcampaign.revokedBy));

    return updatedSubcampaignsList.map(subcampaign => {
      const creatorProfile = subcampaign?.creatorProfile || {};

      return {
        id: creatorProfile.id,
        name: creatorProfile.name,
        offerId: subcampaign?.offer?.id,
        subcampaignState: subcampaign?.state,
        profileImage: creatorProfile.profileImage
      };
    });
  }

  private getSelectionFromQuery(query?: Query | null): SelectionQuery {
    const influencerId = get(query, 'influencerId') || '';
    const deliverableId = get(query, 'deliverableId') || '';
    const taskTypes = get(query, 'taskTypes') || 'instagram_post';
    const sentimentClass = decodeURIComponent(get(query, 'sentimentClass') || '').split(',');

    return {
      influencerId,
      deliverableId,
      sentimentClass: sentimentClass.filter(isDefined),
      taskTypes
    };
  }

  private handleFilterChange = (event: SyntheticEvent<TextEntry>) => {
    const { location } = this.props;
    const name = event.currentTarget.name;
    const value = event.currentTarget.value;

    if (!name) {
      return;
    }

    const selection = this.getSelectionFromQuery(location.query);
    const params = {
      page: 1,
      // $FlowIgnore because of xor
      [name]: xor(selection[name], [value]).join(',')
    };

    navigate.to(getLocationUrl(getLocationWithExtendedQuery(location, params)));
  };

  private handleSelectChange = (value?: string | null, name?: string | null) => {
    const { location } = this.props;

    if (!name) {
      return;
    }

    const selection = this.getSelectionFromQuery(location.query);
    const params: Query = {
      page: 1,
      // $FlowIgnore
      [name]: xor(selection[name], [value]).join(',')
    };

    if (name === 'influencerId') {
      params.deliverableId = '';
    }

    navigate.to(getLocationUrl(getLocationWithExtendedQuery(location, params)));
  };

  private handleTaskTypeChange = (value: string) => {
    const { location } = this.props;

    const params: Query = {
      page: 1,
      taskTypes: value ?? ''
    };

    params.deliverableId = '';

    navigate.to(getLocationUrl(getLocationWithExtendedQuery(location, params)));
  };

  private renderInfluencerSelector(
    influencerProfiles: Array<InfluencerSelectorAlias>,
    selectedInfluencerProfileId?: number | null
  ): React.ReactNode {
    const resetButton = {
      label: __t('admin_sentiment_analysis_filters.buttons.all_creators'),
      icon: 'users-connected-white-bg-custom-fg'
    };

    return (
      <div className="campaign-report-metrics__influencer-selector">
        <InfluencerSelector
          selectedInfluencerProfileId={selectedInfluencerProfileId}
          influencerProfiles={influencerProfiles}
          shouldSort={false}
          resetButton={resetButton}
          onSelect={(influencerId) => this.handleSelectChange(String(influencerId || ''), 'influencerId')}
          showSearch
        />
      </div>
    );
  }

  render() {
    const {
      location,
      isTaskListLoading,
      tasksItems
    } = this.props;

    const selection = this.getSelectionFromQuery(location.query);
    const influencers = this.getInfluencersForSelector();
    const influencerId = selection.influencerId ? Number(selection.influencerId) : null;
    const taskSelectDisabled = (isTaskListLoading || !selection.influencerId);

    return (
      <div className="admin-sentiment-analysis-filters">
        {
          this.renderInfluencerSelector(influencers, influencerId)
        }
        <SentimentAnalysisSecondaryFilter
          isNotFullscreen
          tasksItems={tasksItems}
          selection={selection}
          taskSelectDisabled={taskSelectDisabled}
          onTaskTypeChange={this.handleTaskTypeChange}
          onFilterChange={this.handleFilterChange}
          onSelectChange={this.handleSelectChange}
        />
      </div>
    );
  }
}

const mapStateToProps = (
  { adminSearchDeliverables, router, campaignSubcampaigns }
): StateProps => ({
  subcampaignsList: campaignSubcampaigns.list,
  location: router.location,
  isTaskListLoading: adminSearchDeliverables.isLoading,

  tasksItems: (adminSearchDeliverables.list || []).map((entity) => {
    let datesLabel = '';
    if (entity.additional && entity.startDate) {
      datesLabel = `(${getDisplayDate(entity.startDate)})`;
    } else if (entity.startDate && entity.endDate) {
      datesLabel = `(${getDisplayDateRange(entity.startDate, entity.endDate)})`;
    }

    return ({
      value: String(entity.id),
      label: `${entity.name} ${datesLabel}`
    });
  })
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchSubcampaigns: (campaignId: number) => (
    dispatch(fetchSubcampaigns(campaignId, { showLoadingComponent: false }))
  ),
  dispatchSearchDeliverables: (campaignId, influencerId, deliverableType) =>
    dispatch(searchDeliverablesWithPosts(campaignId, influencerId, deliverableType, true)),
  dispatchResetDeliverables: () => dispatch(resetDeliverables())
});

export default connect(mapStateToProps, mapDispatchToProps)(SentimentAnalysisFilters);
